// src/products/userInvites/pages/ManageInvitesPage.js
// Purpose: Manage and track all sent invitations with filtering, search, and actions

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../config';
import { userInvitesApi } from '../api/userInvitesApi';
import { 
  Search, 
  Filter, 
  Mail, 
  Clock, 
  CheckCircle, 
  XCircle, 
  RefreshCw,
  Calendar,
  User,
  Package,
  AlertTriangle
} from 'lucide-react';

const ManageInvitesPage = ({ user }) => {
  const [invites, setInvites] = useState([]);
  const [filteredInvites, setFilteredInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [message, setMessage] = useState(null);

  const statuses = [
    { id: 'all', name: 'All Invites', count: 0 },
    { id: 'pending', name: 'Pending', count: 0 },
    { id: 'accepted', name: 'Accepted', count: 0 },
    { id: 'expired', name: 'Expired', count: 0 }
  ];

  const products = [
    { id: 'all', name: 'All Products' },
    { id: 'dataroom', name: 'Dataroom' },
    { id: 'capraise', name: 'CapRaise' },
    { id: 'equity', name: 'Equity' },
    { id: 'questions', name: 'Questions' }
  ];

  // Utility functions - memoized to prevent dependency changes
  const isInviteExpired = useCallback((invite) => {
    if (invite.invite_status !== 'pending') return false;
    
    const sentDate = new Date(invite.sentat);
    const expiryDate = new Date(sentDate.getTime() + (7 * 24 * 60 * 60 * 1000)); // 7 days
    return new Date() > expiryDate;
  }, []);

  const getStatusLabel = useCallback((invite) => {
    if (invite.invite_status === 'accepted') return 'Accepted';
    if (isInviteExpired(invite)) return 'Expired';
    return 'Pending';
  }, [isInviteExpired]);

  const getProductLabel = useCallback((product) => {
    const labels = {
      'dataroom': 'Dataroom',
      'capraise': 'CapRaise',
      'equity': 'Equity',
      'questions': 'Questions'
    };
    return labels[product] || product;
  }, []);

  const getDaysAgo = useCallback((dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    return `${diffDays} days ago`;
  }, []);

  // Memoized functions
  const loadInvites = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('userinvites_invites')
        .select('*')
        .eq('invitedby', user.id)
        .order('sentat', { ascending: false });

      if (error) throw error;

      // Process invites to add computed fields
      const processedInvites = data.map(invite => ({
        ...invite,
        isExpired: isInviteExpired(invite),
        statusLabel: getStatusLabel(invite),
        productLabel: getProductLabel(invite.inviteforproduct),
        daysAgo: getDaysAgo(invite.sentat)
      }));

      setInvites(processedInvites);
    } catch (error) {
      console.error('Error loading invites:', error);
      setMessage({ type: 'error', text: 'Failed to load invitations.' });
    } finally {
      setLoading(false);
    }
  }, [user.id, isInviteExpired, getStatusLabel, getProductLabel, getDaysAgo]);

  const filterInvites = useCallback(() => {
    let filtered = [...invites];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(invite =>
        invite.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(invite => {
        const status = getStatusLabel(invite).toLowerCase();
        return status === selectedStatus;
      });
    }

    // Filter by product
    if (selectedProduct !== 'all') {
      filtered = filtered.filter(invite => invite.inviteforproduct === selectedProduct);
    }

    setFilteredInvites(filtered);
  }, [invites, searchTerm, selectedStatus, selectedProduct, getStatusLabel]);

  // Event handlers
  const handleResendInvite = async (invite) => {
    if (!window.confirm(`Are you sure you want to resend the invitation to ${invite.email}?`)) {
      return;
    }

    try {
      setMessage(null);
      
      const result = await userInvitesApi.resendInvite(invite.id);

      if (result.status === 'success') {
        setMessage({ type: 'success', text: result.message || `Invitation resent to ${invite.email}` });
        loadInvites(); // Reload to show updated status
      } else {
        throw new Error(result.message || 'Failed to resend invitation');
      }
    } catch (error) {
      console.error('Error resending invite:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to resend invitation.' });
    }
  };

  const handleRevokeInvite = async (invite) => {
    if (!window.confirm(`Are you sure you want to revoke the invitation for ${invite.email}?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('userinvites_invites')
        .update({ invite_status: 'revoked' })
        .eq('id', invite.id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Invitation revoked successfully.' });
      loadInvites();
    } catch (error) {
      console.error('Error revoking invite:', error);
      setMessage({ type: 'error', text: 'Failed to revoke invitation.' });
    }
  };

  const getStatusIcon = (invite) => {
    const status = getStatusLabel(invite);
    switch (status) {
      case 'Accepted':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'Expired':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-600" />;
    }
  };

  const getStatusBadge = (invite) => {
    const status = getStatusLabel(invite);
    const styles = {
      'Accepted': 'bg-green-100 text-green-800 border-green-200',
      'Expired': 'bg-red-100 text-red-800 border-red-200',
      'Pending': 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status]}`}>
        {status}
      </span>
    );
  };

  // Effects
  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  useEffect(() => {
    filterInvites();
  }, [filterInvites]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Mail className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Manage Invitations</h1>
          </div>
          <button
            onClick={loadInvites}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
        <p className="text-gray-600">
          Track and manage all user invitations you've sent across products.
        </p>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg flex items-center ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.type === 'success' 
            ? <CheckCircle className="w-5 h-5 mr-2" />
            : <AlertTriangle className="w-5 h-5 mr-2" />
          }
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
          
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by email..."
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {statuses.map(status => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>

          {/* Product Filter */}
          <div>
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {products.map(product => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Invites Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {filteredInvites.length === 0 ? (
          <div className="text-center py-12">
            <Mail className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invitations found</h3>
            <p className="text-gray-600">
              {invites.length === 0 
                ? "You haven't sent any invitations yet."
                : "No invitations match your current filters."
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sent
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInvites.map((invite) => (
                  <tr key={invite.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {invite.email}
                          </div>
                          {invite.accepter && (
                            <div className="text-sm text-gray-500">
                              Accepted by {(invite.accepter.user_profiles?.first_name && invite.accepter.user_profiles?.last_name)
                                ? `${invite.accepter.user_profiles.first_name} ${invite.accepter.user_profiles.last_name}`
                                : invite.accepter.user_profiles?.first_name || invite.accepter.user_profiles?.last_name || invite.accepter.email}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Package className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{invite.productLabel}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 capitalize">
                        {invite.role || 'viewer'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(invite)}
                        <span className="ml-2">
                          {getStatusBadge(invite)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        {invite.daysAgo}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-2">
                        {invite.statusLabel === 'Pending' && (
                          <button
                            onClick={() => handleResendInvite(invite)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Resend
                          </button>
                        )}
                        {(invite.statusLabel === 'Pending' || invite.statusLabel === 'Expired') && (
                          <button
                            onClick={() => handleRevokeInvite(invite)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        {statuses.slice(0, 4).map(status => {
          const count = status.id === 'all' 
            ? invites.length 
            : invites.filter(invite => getStatusLabel(invite).toLowerCase() === status.id).length;
          
          return (
            <div key={status.id} className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-2xl font-bold text-gray-900">{count}</div>
              <div className="text-sm text-gray-600">{status.name}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ManageInvitesPage;